"use client";

import React, { useState } from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragOverlay,
    useDroppable,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Divider, Typography } from "@mui/joy";
import { Clock, GripVertical } from "lucide-react";
import moment from "moment";

import { useKanbanBoard } from "./hooks";

type Task = {
    id: string;
    content: string;
    tags: string[];
    assignee: string;
    createdAt: string;
    lastUpdated: string;
};

type Column = {
    id: string;
    title: string;
    tasks: Task[];
};

const COLUMN_STATUS_COLOR: Record<string, string> = {
    "NOT-STARTED": "neutral.400",
    "IN-PROGRESS": "primary.500",
    "IN-REVIEW": "warning.500",
    COMPLETED: "success.500",
};

const COLUMN_STATUS_GLOW: Record<string, string> = {
    "NOT-STARTED": "0 0 8px var(--standard-palette-neutral-400)",
    "IN-PROGRESS": "0 0 8px var(--standard-palette-primary-400)",
    "IN-REVIEW": "0 0 8px var(--standard-palette-warning-400)",
    COMPLETED: "0 0 8px var(--standard-palette-success-400)",
};

const glassPanelSx = {
    border: "1px solid",
    borderColor: "rgba(128, 128, 128, 0.2)",
    borderRadius: "4px",
    bgcolor: "rgba(128, 128, 128, 0.05)",
    backdropFilter: "blur(12px)",
} as const;

interface IKanbanBoard {
    setKanbanTasks: any;
}

function KanbanBoard(props: IKanbanBoard) {
    const [activeTask, setActiveTask] = useState<Task | null>(null);
    const { setKanbanTasks } = { ...props };

    const { columns, setColumns, handleTaskDbUpdate } = useKanbanBoard();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
    );

    const findTaskById = (id: string) => {
        for (const column of columns) {
            const task = column.tasks.find((t) => t.id === id);
            if (task) return { task, column };
        }
        return null;
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveTask(null);

        if (!over || active.id === over.id) return;

        const sourceInfo = findTaskById(active.id as string);
        const targetInfo = findTaskById(over.id as string);

        if (!sourceInfo) return;
        const sourceCol = sourceInfo.column;
        const taskToMove = sourceInfo.task;

        const updatedSourceTasks = sourceCol.tasks.filter((t) => t.id !== active.id);

        let updatedColumns = [...columns];

        if (targetInfo) {
            const targetCol = targetInfo.column;
            const targetIndex = targetCol.tasks.findIndex((t) => t.id === over.id);
            const movedTask = {
                ...taskToMove,
                taskSummary: {
                    ...(taskToMove as any).taskSummary,
                    Status: targetCol.id,
                },
                lastUpdated: new Date().toISOString(),
            };
            const updatedTargetTasks = [...targetCol.tasks];
            updatedTargetTasks.splice(targetIndex, 0, movedTask);

            updatedColumns = updatedColumns.map((col) => {
                if (col.id === sourceCol.id) return { ...col, tasks: updatedSourceTasks };
                if (col.id === targetCol.id) return { ...col, tasks: updatedTargetTasks };
                return col;
            });
            handleTaskDbUpdate({ ...(movedTask as any).taskSummary, Status: targetCol.id });
        } else {
            const targetCol = columns.find((col) => col.id === over.id);

            if (!targetCol) return;
            if (sourceCol.id === targetCol.id) return;

            const movedTask = {
                ...taskToMove,
                taskSummary: {
                    ...(taskToMove as any).taskSummary,
                    Status: targetCol.id,
                },
                lastUpdated: new Date().toISOString(),
            };
            const updatedTargetTasks = [...targetCol.tasks, movedTask];

            updatedColumns = updatedColumns.map((col) => {
                if (col.id === sourceCol.id) return { ...col, tasks: updatedSourceTasks };
                if (col.id === targetCol.id) return { ...col, tasks: updatedTargetTasks };
                return col;
            });
            handleTaskDbUpdate({ ...(movedTask as any).taskSummary, Status: targetCol.id });
        }

        setColumns(updatedColumns);
        setKanbanTasks(updatedColumns);
    };

    return (
        <Box>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                onDragStart={(event) => {
                    const taskInfo = findTaskById(event.active.id as string);
                    if (taskInfo) setActiveTask(taskInfo.task);
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "minmax(240px, 1fr)",
                            sm: "repeat(2, minmax(220px, 1fr))",
                            md: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 2,
                        overflowX: "auto",
                        pb: 1,
                    }}
                >
                    {columns.map((column) => (
                        <DroppableColumn key={column.id} column={column}>
                            <SortableContext
                                items={column.tasks.map((t) => t.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {column.tasks.length === 0 ? (
                                    <Box
                                        sx={{
                                            ...glassPanelSx,
                                            py: 3,
                                            px: 1.5,
                                            textAlign: "center",
                                            borderStyle: "dashed",
                                        }}
                                    >
                                        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                            Drop tasks here
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box display="flex" flexDirection="column" gap={1}>
                                        {column.tasks.map((task) => (
                                            <SortableTask key={task.id} task={task} />
                                        ))}
                                    </Box>
                                )}
                            </SortableContext>
                        </DroppableColumn>
                    ))}
                </Box>
                <DragOverlay>
                    {activeTask ? <TaskCardView task={activeTask} isOverlay /> : null}
                </DragOverlay>
            </DndContext>
        </Box>
    );
}

function DroppableColumn({ column, children }: { column: Column; children: React.ReactNode }) {
    const { setNodeRef, isOver } = useDroppable({ id: column.id });
    const statusColor = COLUMN_STATUS_COLOR[column.id] || "neutral.400";
    const statusGlow = COLUMN_STATUS_GLOW[column.id];

    return (
        <Box
            ref={setNodeRef}
            sx={{
                ...glassPanelSx,
                p: 1.25,
                minHeight: 200,
                transition: "border-color 0.15s ease, background-color 0.15s ease",
                ...(isOver && {
                    borderColor: "primary.outlinedBorder",
                    bgcolor: "rgba(var(--standard-palette-primary-mainChannel) / 0.06)",
                }),
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1.25,
                    px: 0.25,
                }}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: statusColor,
                            boxShadow: statusGlow,
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        level="body-xs"
                        sx={{
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 600,
                            color: "text.tertiary",
                        }}
                    >
                        {column.title}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        minWidth: 22,
                        height: 22,
                        px: 0.75,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "4px",
                        border: "1px solid",
                        borderColor: "rgba(128, 128, 128, 0.2)",
                        bgcolor: "background.surface",
                    }}
                >
                    <Typography level="body-xs" sx={{ fontWeight: 600, color: "text.secondary" }}>
                        {column.tasks.length}
                    </Typography>
                </Box>
            </Box>

            {children}
        </Box>
    );
}

function TaskCardView({
    task,
    isOverlay,
    dragHandleProps,
}: {
    task: Task;
    isOverlay?: boolean;
    dragHandleProps?: Record<string, unknown>;
}) {
    const description = task.tags?.[0];

    return (
        <Box
            {...dragHandleProps}
            sx={{
                ...glassPanelSx,
                bgcolor: "background.surface",
                p: 1.25,
                cursor: isOverlay ? "grabbing" : "grab",
                transition: "box-shadow 0.15s ease, border-color 0.15s ease",
                ...(isOverlay && {
                    boxShadow: "md",
                    borderColor: "primary.outlinedBorder",
                }),
                "&:hover": {
                    borderColor: "rgba(128, 128, 128, 0.35)",
                },
            }}
        >
            <Box display="flex" alignItems="flex-start" gap={1}>
                <Box sx={{ mt: 0.25, opacity: 0.35, flexShrink: 0 }}>
                    <GripVertical size={14} />
                </Box>
                <Box minWidth={0} flex={1}>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                        {task.content}
                    </Typography>
                    {description ? (
                        <Typography
                            level="body-xs"
                            sx={{ color: "text.secondary", mt: 0.25 }}
                            noWrap
                        >
                            {description}
                        </Typography>
                    ) : null}
                </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box display="flex" alignItems="center" gap={1.5}>
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Clock size={12} opacity={0.55} />
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {moment(task.lastUpdated || task.createdAt).fromNow()}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

function SortableTask({ task }: { task: Task }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: task.id,
    });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.4 : 1,
            }}
        >
            <TaskCardView task={task} dragHandleProps={{ ...attributes, ...listeners }} />
        </Box>
    );
}

export default KanbanBoard;
